/**
 * Plant filtering utilities for client-side catalog filtering.
 * All functions are pure and designed for use with useMemo.
 */

import { compareZones, parseZone } from './hardinessZone';

/**
 * Enum mappings for German UI labels
 */
export const GROWTH_TYPE_LABELS = {
  annual: 'Einjährig',
  perennial: 'Staude',
  shrub: 'Strauch',
  tree: 'Baum',
  grass: 'Gras',
  vine: 'Kletterpflanze',
  bulb: 'Zwiebelpflanze',
  herb: 'Kraut',
  vegetable: 'Gemüse',
  lawn: 'Rasen',
  houseplant: 'Zimmerpflanze'
};

export const GROWTH_SPEED_LABELS = {
  slow: 'Langsam (5-20 cm/Jahr)',
  medium: 'Mittel (15-30 cm/Jahr)',
  fast: 'Schnell (30-200+ cm/Jahr)'
};

export const CARE_PRIORITY_LABELS = {
  low: 'Pflegeleicht',
  medium: 'Mittel',
  high: 'Intensiv'
};

/**
 * Enum mappings for low-priority filters
 */
export const SOIL_TYPE_LABELS = {
  loamy: 'Lehmig',
  sandy: 'Sandig',
  chalky: 'Kalkhaltig',
  acidic: 'Sauer',
  humus: 'Humusreich',
  'well-drained': 'Durchlässig',
  wet: 'Feucht',
  organic: 'Organisch'
};

export const TOXICITY_LEVEL_LABELS = {
  none: 'Ungiftig',
  low: 'Leicht giftig',
  medium: 'Mäßig giftig',
  high: 'Stark giftig',
  severe: 'Lebensgefährlich'
};

export const TOXICITY_LEVEL_COLORS = {
  none: '#10B981',    // emerald-500
  low: '#F59E0B',     // amber-500
  medium: '#F97316',  // orange-500
  high: '#EF4444',    // red-500
  severe: '#991B1B'   // red-800
};

export const SCENT_INTENSITY_LABELS = {
  none: 'Kein Duft',
  light: 'Leicht',
  medium: 'Mittel',
  strong: 'Stark'
};

export const ROOT_TYPE_LABELS = {
  shallow: 'Flachwurzler',
  deep: 'Tiefwurzler',
  heart: 'Herzwurzler'
};

export const ROOT_TYPE_TOOLTIPS = {
  shallow: 'Wurzeln 0-40cm, Oberflächenwasser',
  deep: 'Pfahlwurzel >1m, trockenheitsresistent',
  heart: 'Gleichmäßig in alle Richtungen'
};

export const MONTH_LABELS = {
  1: 'Jan', 2: 'Feb', 3: 'Mär', 4: 'Apr', 5: 'Mai', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Okt', 11: 'Nov', 12: 'Dez'
};

/**
 * Filter plants by search query (name or scientific_name)
 * @param {Array} plants - Array of plant objects
 * @param {string} query - Search query (min 2 chars)
 * @returns {Array} Filtered plants
 */
export const filterBySearch = (plants, query) => {
  if (!query || query.trim().length < 2) return plants;

  const searchLower = query.trim().toLowerCase();
  return plants.filter(plant =>
    (plant.name && plant.name.toLowerCase().includes(searchLower)) ||
    (plant.scientific_name && plant.scientific_name.toLowerCase().includes(searchLower))
  );
};

/**
 * Filter plants by sunlight requirement (OR logic within category)
 * v7: sunlight is now stored as an array (e.g., ["full_sun", "partial_shade"])
 * A plant matches if ANY of its sunlight values matches ANY of the filter values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected sunlight values
 * @returns {Array} Filtered plants
 */
export const filterBySunlight = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const sunlight = plant.properties?.sunlight || plant.sunlight;
    // v7: sunlight can be array or string (backwards compatibility)
    const sunlightArray = Array.isArray(sunlight) ? sunlight : [sunlight];
    // Check if any plant sunlight value matches any filter value
    return sunlightArray.some(s => values.includes(s));
  });
};

/**
 * Filter plants by water requirement (OR logic within category)
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected water requirement values
 * @returns {Array} Filtered plants
 */
export const filterByWaterRequirement = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const water = plant.properties?.water_requirement || plant.water_requirement;
    return values.includes(water);
  });
};

/**
 * Filter plants by toxicity (AND logic - all selected filters must match)
 * - non_toxic: NOT toxic to pets AND NOT toxic to children
 * - safe_for_pets: NOT toxic to pets (inverted logic)
 * - safe_for_children: NOT toxic to children (inverted logic)
 *
 * When multiple filters are selected, ALL conditions must be met (AND logic).
 * Example: safe_for_pets + safe_for_children = plants safe for both
 *
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected toxicity filter values
 * @returns {Array} Filtered plants
 */
export const filterByToxicity = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const props = plant.properties || {};
    const toxicToPets = props.toxic_to_pets ?? plant.toxic_to_pets ?? false;
    const toxicToChildren = props.toxic_to_children ?? plant.toxic_to_children ?? false;

    // Check each selected filter (AND logic - all must match)
    for (const value of values) {
      if (value === 'non_toxic' && (toxicToPets || toxicToChildren)) return false;
      if (value === 'safe_for_pets' && toxicToPets) return false;
      if (value === 'safe_for_children' && toxicToChildren) return false;
    }
    return true;
  });
};

/**
 * Filter plants by flower color (OR logic - plant has ANY of selected colors)
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected flower color values
 * @returns {Array} Filtered plants
 */
export const filterByFlowerColor = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const colors = plant.properties?.flower_color || [];
    return values.some(color => colors.includes(color));
  });
};

/**
 * Filter plants by boolean property from properties object
 * @param {Array} plants - Array of plant objects
 * @param {string} propertyName - Property name in properties object
 * @param {Array} values - Selected values (contains 'true' to filter)
 * @returns {Array} Filtered plants
 */
export const filterByBooleanProperty = (plants, propertyName, values) => {
  if (!values || values.length === 0 || !values.includes('true')) return plants;

  return plants.filter(plant => {
    return plant.properties?.[propertyName] === true;
  });
};

/**
 * Filter plants by bee_friendly rating (high or medium)
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected values (contains 'high' to filter)
 * @returns {Array} Filtered plants
 */
export const filterByBeeFriendly = (plants, values) => {
  if (!values || values.length === 0 || !values.includes('high')) return plants;

  return plants.filter(plant => {
    const rating = plant.properties?.bee_friendly;
    return rating === 'high' || rating === 'medium';
  });
};

/**
 * Filter plants by growth type (properties.growth_type)
 * OR logic - plant matches if its growth_type is in selected values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected growth type values (e.g., ['annual', 'perennial'])
 * @returns {Array} Filtered plants
 */
export const filterByGrowthType = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const growthType = plant.properties?.growth_type;
    return values.includes(growthType);
  });
};

/**
 * Filter plants by growth speed (growth_speed)
 * OR logic - plant matches if its speed is in selected values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected growth speed values (e.g., ['slow', 'medium'])
 * @returns {Array} Filtered plants
 */
export const filterByGrowthSpeed = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const speed = plant.properties?.growth_speed;
    return values.includes(speed);
  });
};

/**
 * Filter plants by maximum growth height (upper bound "bis X cm").
 * Keeps plants whose max_height_cm is at or below the chosen limit.
 * The height lives flat on the plant or under properties (mirrors displayHeight
 * in PlantDetailSheet). Plants without a height value are excluded while the
 * filter is active, since we cannot guarantee they stay within the limit.
 * @param {Array} plants - Array of plant objects
 * @param {number|null} maxCm - Upper bound in cm, or null for no filter
 * @returns {Array} Filtered plants
 */
export const filterByMaxHeight = (plants, maxCm) => {
  if (maxCm == null) return plants;

  return plants.filter(plant => {
    const height = plant.max_height_cm ?? plant.properties?.max_height_cm;
    if (height == null) return false;
    return height <= maxCm;
  });
};

/**
 * Filter plants WITHOUT thorns (has_thorns)
 * INVERTED LOGIC - shows plants where has_thorns is NOT true
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected values (contains 'true' to activate filter)
 * @returns {Array} Filtered plants
 */
export const filterByNoThorns = (plants, values) => {
  if (!values || values.length === 0 || !values.includes('true')) return plants;

  return plants.filter(plant => {
    return plant.properties?.has_thorns !== true;
  });
};

/**
 * Filter plants by care priority level (care_instructions.priority)
 * OR logic - plant matches if its priority is in selected values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected care priority values (e.g., ['low', 'medium'])
 * @returns {Array} Filtered plants
 */
export const filterByCarePriority = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const priority = plant.care_instructions?.priority;
    return values.includes(priority);
  });
};

/**
 * Filter plants that are climbers (is_climber)
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected values (contains 'true' to filter)
 * @returns {Array} Filtered plants
 */
export const filterByClimber = (plants, values) => {
  if (!values || values.length === 0 || !values.includes('true')) return plants;

  return plants.filter(plant => {
    return plant.properties?.is_climber === true;
  });
};

/**
 * Filter plants by pH range compatibility (ph_range)
 * Shows plants where user's pH value falls within plant's pH range
 * @param {Array} plants - Array of plant objects
 * @param {number} userPh - User's soil pH value (4.0-8.5)
 * @returns {Array} Filtered plants
 */
export const filterByPhRange = (plants, userPh) => {
  if (!userPh || isNaN(userPh)) return plants;

  return plants.filter(plant => {
    const phRange = plant.properties?.ph_range;

    // If plant has no pH restriction, include it
    if (!phRange) return true;

    // Parse range format "MIN-MAX"
    const [minStr, maxStr] = phRange.split('-');
    const min = parseFloat(minStr);
    const max = parseFloat(maxStr);

    // Check if user pH is within plant's tolerance range
    return userPh >= min && userPh <= max;
  });
};

/**
 * Filter plants by soil type (properties.soil_type)
 * OR logic - plant matches if it supports ANY of the selected soil types
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected soil type values (e.g., ['loamy', 'sandy'])
 * @returns {Array} Filtered plants
 */
export const filterBySoilType = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const soilTypes = plant.properties?.soil_type || [];
    // soil_type is already an array in the database
    const soilArray = Array.isArray(soilTypes) ? soilTypes : [soilTypes];

    // Plant matches if it supports ANY of the selected soil types
    return values.some(selectedType => soilArray.includes(selectedType));
  });
};

/**
 * Filter plants by detailed toxicity level (properties.toxicity_level)
 * OR logic - plant matches if its level is in selected values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected toxicity levels (e.g., ['none', 'low'])
 * @returns {Array} Filtered plants
 */
export const filterByToxicityLevel = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const level = plant.properties?.toxicity_level || 'none';
    return values.includes(level);
  });
};

/**
 * Filter plants by scent intensity (properties.scent_intensity)
 * OR logic - plant matches if its scent intensity is in selected values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected scent intensities (e.g., ['medium', 'strong'])
 * @returns {Array} Filtered plants
 */
export const filterByScentIntensity = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const scent = plant.properties?.scent_intensity || 'none';
    return values.includes(scent);
  });
};

/**
 * Filter plants by root type (properties.root_type)
 * OR logic - plant matches if its root type is in selected values
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected root types (e.g., ['shallow', 'deep'])
 * @returns {Array} Filtered plants
 */
export const filterByRootType = (plants, values) => {
  if (!values || values.length === 0) return plants;

  return plants.filter(plant => {
    const rootType = plant.properties?.root_type;
    return values.includes(rootType);
  });
};

/**
 * Filter plants WITHOUT winter protection requirement (care_instructions.winter_protection_needed)
 * INVERTED LOGIC - shows plants where winter_protection_needed is NOT true
 * @param {Array} plants - Array of plant objects
 * @param {Array} values - Selected values (contains 'true' to activate filter)
 * @returns {Array} Filtered plants
 */
export const filterByNoWinterProtection = (plants, values) => {
  if (!values || values.length === 0 || !values.includes('true')) return plants;

  return plants.filter(plant => {
    return plant.care_instructions?.winter_protection_needed !== true;
  });
};

/**
 * Filter plants by flowering months (properties.flowering_months)
 * Shows plants that flower in AT LEAST ONE of the selected months
 * @param {Array} plants - Array of plant objects
 * @param {Object} monthRange - Month range {start: 1, end: 12} or null
 * @returns {Array} Filtered plants
 */
export const filterByFloweringMonths = (plants, monthRange) => {
  if (!monthRange || !monthRange.start || !monthRange.end) return plants;

  // Generate array of selected months from range
  const selectedMonths = [];

  // Handle year wrapping (e.g., Nov-Feb = [11, 12, 1, 2])
  if (monthRange.start <= monthRange.end) {
    // Normal range (e.g., 3-8)
    for (let m = monthRange.start; m <= monthRange.end; m++) {
      selectedMonths.push(m);
    }
  } else {
    // Wrapped range (e.g., 11-2 = Nov, Dec, Jan, Feb)
    for (let m = monthRange.start; m <= 12; m++) {
      selectedMonths.push(m);
    }
    for (let m = 1; m <= monthRange.end; m++) {
      selectedMonths.push(m);
    }
  }

  return plants.filter(plant => {
    const floweringMonths = plant.properties?.flowering_months;

    // If plant has no flowering data, exclude it
    if (!floweringMonths || !Array.isArray(floweringMonths) || floweringMonths.length === 0) {
      return false;
    }

    // Plant matches if it flowers in AT LEAST ONE selected month
    return floweringMonths.some(month => selectedMonths.includes(month));
  });
};

/**
 * Filter plants by pet-specific toxicity (properties.toxic_to_cats, toxic_to_dogs)
 * Separate filters for cats and dogs (can be used independently or together)
 * INVERTED LOGIC - shows plants NOT toxic to selected animal types
 * @param {Array} plants - Array of plant objects
 * @param {Object} filters - Filter state { safe_for_cats: ['true'], safe_for_dogs: ['true'] }
 * @returns {Array} Filtered plants
 */
export const filterByPetSpecific = (plants, filters) => {
  let result = plants;

  // Filter for cat-safe plants
  if (filters.safe_for_cats && filters.safe_for_cats.includes('true')) {
    result = result.filter(p => p.properties?.toxic_to_cats !== true);
  }

  // Filter for dog-safe plants
  if (filters.safe_for_dogs && filters.safe_for_dogs.includes('true')) {
    result = result.filter(p => p.properties?.toxic_to_dogs !== true);
  }

  return result;
};

/**
 * Mapping from Wizard soil type values to catalog DB soil type values
 * Wizard: humus, loamy, loess, mixed, sandy, clay
 * DB:     humus, loamy, sandy, well-drained, acidic, chalky, wet, organic
 */
export const WIZARD_TO_CATALOG_SOIL_MAP = {
  humus: ['humus'],
  loamy: ['loamy'],
  sandy: ['sandy', 'well-drained'],   // Sandboden ist gut durchlässig
  clay: ['loamy', 'wet'],             // Tonboden ≈ lehmig + feucht (kein "clay" in DB)
  loess: ['loamy', 'chalky'],         // Löss = lehmig-kalkhaltig
  mixed: ['loamy', 'sandy', 'humus'], // Mischboden = breiter Match
};

/**
 * Filter plants by hardiness zone match.
 * Removes plants that are definitively too cold for the given garden zone.
 * Plants with 'ok' or 'marginal' status, or missing zone data, are kept.
 *
 * @param {Array} plants
 * @param {string|undefined} gardenZone - e.g. "Zone 9b"
 * @returns {Array}
 */
export function filterByHardinessZoneMatch(plants, gardenZone) {
  if (!gardenZone) return plants;
  return plants.filter(plant => {
    const plantZone =
      plant.care_instructions?.min_hardiness_zone || plant.properties?.min_hardiness_zone;
    return compareZones(plantZone, gardenZone) !== 'too_cold';
  });
}

/**
 * Build catalog filter object from garden settings (from wizard/GardenSettingsContext)
 * Maps wizard values to catalog filter format
 * @param {Object} settings - Garden settings from GardenSettingsContext
 * @returns {Object} Filter object for use in PlantCatalogDialog
 */
export function buildGardenFilters(settings) {
  if (!settings) return {};

  const filters = {};

  // Sonnenlicht (direkt im DB-Format gespeichert)
  if (settings.sunlight) {
    filters.sunlight = [settings.sunlight];
  }

  // Bodentyp (Wizard → DB-Werte mappen)
  if (settings.soilType && WIZARD_TO_CATALOG_SOIL_MAP[settings.soilType]) {
    filters.soil_type = WIZARD_TO_CATALOG_SOIL_MAP[settings.soilType];
  }

  // pH-Wert (direkt übernehmen)
  if (settings.phValue != null) {
    filters.ph_value = settings.phValue;
  }

  // Drainage → Wasserbedarf (Approximation)
  if (settings.drainage) {
    const drainageMap = {
      good: ['low', 'medium'],
      medium: ['low', 'medium', 'high'],
      poor: ['medium', 'high'],
    };
    filters.water_requirement = drainageMap[settings.drainage] || [];
  }

  // Winterhärtezone-Matching (wenn Winterhärtezone gesetzt)
  if (settings.hardinessZone) {
    if (parseZone(settings.hardinessZone)) {
      // Präzises Subzonen-Matching — filtert too_cold-Pflanzen heraus
      filters.hardiness_zone_match = settings.hardinessZone;
    } else {
      // Fallback für altes Format ohne Subzonen-Buchstaben (z. B. "Zone 7")
      filters.frost_hardy = ['true'];
    }
  }

  return filters;
}

/**
 * Sort plants by name
 * @param {Array} plants - Array of plant objects
 * @param {string} sortBy - Sort direction ('name_asc' or 'name_desc')
 * @returns {Array} Sorted plants (new array)
 */
export const sortPlants = (plants, sortBy) => {
  const sorted = [...plants];

  if (sortBy === 'name_asc') {
    sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  } else if (sortBy === 'name_desc') {
    sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
  }

  return sorted;
};

/**
 * Apply all filters and sorting to plants array
 * @param {Array} plants - Array of all plant objects
 * @param {Object} filters - Filter state object
 * @param {string} searchQuery - Search query string
 * @param {string} sortBy - Sort option
 * @returns {Array} Filtered and sorted plants
 */
export const applyAllFilters = (plants, filters, searchQuery, sortBy) => {
  let result = plants;

  // Search filter
  result = filterBySearch(result, searchQuery);

  // Category filters
  result = filterBySunlight(result, filters.sunlight);
  result = filterByWaterRequirement(result, filters.water_requirement);
  result = filterByToxicity(result, filters.toxic);
  result = filterByFlowerColor(result, filters.flower_color);

  // Boolean property filters
  result = filterByBooleanProperty(result, 'evergreen', filters.evergreen);
  result = filterByBeeFriendly(result, filters.bee_friendly);
  result = filterByBooleanProperty(result, 'frost_hardy', filters.frost_hardy);
  result = filterByHardinessZoneMatch(result, filters.hardiness_zone_match);
  result = filterByBooleanProperty(result, 'drought_tolerant', filters.drought_tolerant);
  result = filterByBooleanProperty(result, 'suitable_for_pot', filters.suitable_for_pot);
  result = filterByBooleanProperty(result, 'suitable_for_planter', filters.suitable_for_planter);

  // NEW FILTERS (High Priority)
  result = filterByGrowthType(result, filters.care_growth_type);
  result = filterByGrowthSpeed(result, filters.growth_speed);
  result = filterByNoThorns(result, filters.has_thorns);
  result = filterByCarePriority(result, filters.care_priority);
  result = filterByClimber(result, filters.is_climber);
  result = filterByMaxHeight(result, filters.max_height_cm);
  result = filterByPhRange(result, filters.ph_value);

  // NEW LOW-PRIORITY FILTERS
  result = filterBySoilType(result, filters.soil_type);
  result = filterByToxicityLevel(result, filters.toxicity_level);
  result = filterByScentIntensity(result, filters.scent_intensity);
  result = filterByRootType(result, filters.root_type);
  result = filterByNoWinterProtection(result, filters.winter_protection);
  result = filterByFloweringMonths(result, filters.flowering_months);
  result = filterByPetSpecific(result, filters);

  // Sorting
  result = sortPlants(result, sortBy);

  return result;
};